/**
 * The bell, and the panel behind it.
 *
 * The prototype drew a bell with nothing behind it and `docs/BACKLOG.md` listed
 * it under "drawn but not functional". This is the thing behind it.
 *
 * ── The wording is composed here, not on the server ─────────────────────────
 * A notification arrives as a *fact*: a kind, an actor, and the ids needed to
 * build a link. The server cannot phrase it, because a workspace's name and a
 * file's name are encrypted under keys it does not hold — "Ayse added you to
 * Marketing" is a sentence only a member's browser can write.
 *
 * So the copy here is deliberately name-free: "added you to a workspace", not
 * "added you to X". Resolving the name would mean fetching and decrypting the
 * workspace on the strength of a bell being opened, which is a lot of work for
 * a line of text — and would leave rows for workspaces the reader has since
 * been removed from rendering as a decryption failure. Following the link is
 * what shows them the name, from a screen that already has the key.
 *
 * ── Polling, like Chat ──────────────────────────────────────────────────────
 * Same reasoning as `Chat.tsx`: a Durable Object would give real push and also
 * add a binding, a migration and a second thing to operate. Slower than chat's
 * five seconds because a notification is not a conversation, and paused
 * entirely when the tab is hidden.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type NotificationWire } from '../api';
import { ds } from '../ds';
import { Link } from '../router';
import { useT } from '../i18n';

const POLL_MS = 60_000;
const HIDDEN_POLL_MS = 5 * 60_000;

/** What each kind says, and where it goes. */
function describe(n: NotificationWire): { text: string; href: string | null } {
  // A Review reviewer typed their own name and nobody checked it, so it is
  // marked rather than rendered as though we had. Without this, a name is two
  // form fields away from impersonating anyone inside an authenticated UI.
  const raw = n.actor?.name?.trim() || n.actor?.email || 'Someone';
  const who = n.actor && n.actor.verified === false ? `${raw} (unverified)` : raw;

  switch (n.kind) {
    case 'workspace_invite':
      return {
        text: `${who} added you to a workspace`,
        href: n.workspaceId ? `/workspaces/${n.workspaceId}` : '/workspaces'
      };
    case 'workspace_mention':
      return {
        text: `${who} mentioned you`,
        href: n.workspaceId ? `/workspaces/${n.workspaceId}` : null
      };
    case 'share_invite':
      return { text: `${who} shared a file with you`, href: '/shared' };
    case 'key_ready':
      return { text: `${who} finished sharing — it is ready to open`, href: '/shared' };

    // Pushed in by Mauvely Review. `ref` is `<projectId>/<threadId>`, opaque to
    // this database; the URL is built here rather than stored, the same way
    // `courseUrl()` derives the courses host from this one. `Link` already
    // sends a foreign origin to `window.location.href`, so an absolute URL
    // needs no special handling.
    case 'review_comment':
    case 'review_reply':
    case 'review_mention': {
      const verb =
        n.kind === 'review_reply'
          ? 'replied on'
          : n.kind === 'review_mention'
            ? 'tagged you on'
            : 'commented on';
      const what = n.subject ? `${verb} ${n.subject}` : verb.replace(' on', '');
      return { text: `${who} ${what}`, href: reviewUrl(n.ref ?? null) };
    }
    case 'review_approved':
      return {
        text: `${who} approved ${n.subject ?? 'a review'}`,
        href: reviewUrl(n.ref ?? null)
      };

    // Pushed in by Mauvely Learn. `ref` is a path in that app — `/course/<id>`
    // — so the host is swapped and the path appended, the same shape as the
    // Review links above.
    case 'lms_assigned':
      return {
        text: `${who} assigned you ${n.subject ?? 'a course'}`,
        href: lmsUrl(n.ref ?? null)
      };
    case 'lms_due_soon':
      return { text: `${n.subject ?? 'A course'} is due soon`, href: lmsUrl(n.ref ?? null) };
    case 'lms_overdue':
      return { text: `${n.subject ?? 'A course'} is overdue`, href: lmsUrl(n.ref ?? null) };

    default:
      // An unknown kind renders rather than breaking the list: a newer server
      // may send one this build has never heard of.
      return { text: `${who} did something`, href: null };
  }
}

/**
 * A Review comment's address.
 *
 * Derived from this host rather than configured, so a staging Cloud points at a
 * staging Review without a second variable. Falls back to production, which is
 * the same failure mode `courseUrl()` chooses: a link to the wrong environment
 * is better than a link to nowhere.
 */
function reviewUrl(ref: string | null): string {
  const host = window.location.hostname.replace(/^cloud\./, 'review.');
  const base = host.startsWith('review.') ? `https://${host}` : 'https://review.mauvely.com';
  if (!ref) return base;
  const [projectId, threadId] = ref.split('/');
  return threadId ? `${base}/p/${projectId}#c-${threadId}` : `${base}/p/${projectId}`;
}

/**
 * A course's address in Mauvely Learn.
 *
 * The host is derived from this one, like `reviewUrl` above. What is different
 * is the `ref`: Learn sends a **path** rather than a pair of ids, so it is
 * checked before it is appended. A row in this database is written by another
 * Worker, and a path that began `//` or carried a scheme would leave the ref a
 * way to point a link in an authenticated UI anywhere at all.
 */
const LMS_REF = /^\/[A-Za-z0-9_\-/]{0,199}$/;

function lmsUrl(ref: string | null): string {
  const host = window.location.hostname.replace(/^cloud\./, 'lms.');
  const base = host.startsWith('lms.') ? `https://${host}` : 'https://lms.mauvely.com';
  if (!ref || !LMS_REF.test(ref) || ref.startsWith('//')) return base;
  return `${base}${ref}`;
}

function ago(seconds: number): string {
  const d = Math.max(0, Math.floor(Date.now() / 1000) - seconds);
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export function NotificationBell() {
  const t = useT();
  const { Icon } = ds();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationWire[]>([]);
  const [unread, setUnread] = useState(0);
  const wrap = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.notifications.list();
      setItems(res.notifications);
      setUnread(res.unread);
    } catch {
      // A failed poll is not worth surfacing. The next one will succeed, and a
      // red banner because a bell could not refresh is worse than a stale count.
    }
  }, []);

  useEffect(() => {
    void refresh();
    let timer = window.setInterval(
      () => void refresh(),
      document.visibilityState === 'visible' ? POLL_MS : HIDDEN_POLL_MS
    );
    const onVisibility = () => {
      window.clearInterval(timer);
      timer = window.setInterval(
        () => void refresh(),
        document.visibilityState === 'visible' ? POLL_MS : HIDDEN_POLL_MS
      );
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  // Same dismissal contract as AccountChip: outside mousedown, and Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markAllRead = async () => {
    // Optimistic: the badge should clear on the click, not a round trip later.
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? Date.now() / 1000 })));
    try {
      await api.notifications.read();
    } catch {
      void refresh();
    }
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          border: 'none',
          background: 'transparent',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <Icon name="bell" size={16} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 8,
              height: 8,
              borderRadius: 999,
              background: 'var(--accent-600)',
              // A dot, not a number. The count is in the panel and on the
              // aria-label; a two-digit badge on a 34px button is unreadable.
              // The ring cuts the dot out of what it sits on, which since
              // 2026-09-09 is the app ground rather than a white top bar.
              boxShadow: '0 0 0 2px var(--app-ground)'
            }}
          />
        )}
      </button>

      {open && (
        <div
          className="mv-pop"
          style={{
            position: 'absolute',
            top: 44,
            right: 0,
            width: 'min(320px, calc(100vw - 32px))',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 16,
            boxShadow: '0 18px 44px rgba(40,28,45,.18)',
            zIndex: 6,
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderBottom: '1px solid var(--border-hairline)'
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>
              {t('notification-bell.notifications')}
            </span>
            {unread > 0 && (
              <button
                onClick={() => void markAllRead()}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--accent-600)',
                  fontSize: 12,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {t('notification-bell.mark-all-read')}
              </button>
            )}
          </div>

          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <p style={{ margin: 0, padding: '18px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
                {t('notification-bell.nothing-yet')}
              </p>
            ) : (
              items.map((n) => {
                const { text, href } = describe(n);
                const row = (
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      padding: '11px 14px',
                      background: n.readAt ? 'transparent' : 'var(--surface-sunken)',
                      cursor: href ? 'pointer' : 'default'
                    }}
                  >
                    <span style={{ fontSize: 13, color: 'var(--text-strong)', lineHeight: 1.4 }}>
                      {text}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {ago(n.createdAt)}
                    </span>
                  </span>
                );

                return href ? (
                  <Link key={n.id} to={href} onClick={() => setOpen(false)}>
                    {row}
                  </Link>
                ) : (
                  <div key={n.id}>{row}</div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
